import UserSetting from '@/common/models/user/UserSetting';
import { OrgAppState } from '@/org-app/store/org-app-store-types';
import { MappedPostAction } from '@/utils/ActionsUtils';
import { EntityWithID } from '@/utils/ApiUtils';
import LocalCache from '@/utils/LocalCache';
import Nullable from '@/utils/Nullable';
import { mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';

/**
 * If set to `true`, the data stored in the LocalStorage will be gradually migrated to the UserSettings API as the `get` and `set` method are called.
 * @example Calling get('abc') will save the abc value in the user settings in the database and will be removed from the LocalStorage.
 * @note This variable can eventually be removed when we are confident that enough of the user local data have been migrated to the User Settings API.
 */
const MIGRATE_LOCAL_CACHE_TO_USER_SETTINGS_API = true;

class SettingWrapper<T> {
  private deleteLocked = false;

  private state: OrgAppState;
  private createUserSetting: MappedPostAction<UserSetting>;
  private deleteUserSetting: (args: {
    instance: EntityWithID;
    showLoader: boolean;
    shouldWarnUserOnError: boolean;
  }) => Promise<void>;

  constructor(
    public readonly key: string,
    private readonly stringify: (val: T) => string = JSON.stringify,
    private readonly parse: (val: string) => T = JSON.parse,
  ) {
    this.state = mapOrgAppState();
    const { createUserSetting, deleteUserSetting } = mapOrgAppActions();
    this.createUserSetting = createUserSetting;
    this.deleteUserSetting = deleteUserSetting;
  }

  private getStoredSetting(): Nullable<UserSetting> {
    const userSettings = this.state.UserSettings.userSettings;
    return userSettings?.find((setting) => setting.key === this.key);
  }

  private async cleanupLocalStorageOnSet() {
    const localValue = LocalCache.get(this.key);
    try {
      if (localValue != null) {
        await this.createUserSetting({
          instances: new UserSetting(this.key, this.stringify(localValue)),
          showLoader: false,
        });
      }
      // if saving in database successful, remove from local storage
      LocalCache.remove(this.key);
    } catch (error) {
      console.error('Failed to save setting to database', error);
    }
  }

  public get(): T {
    // Get value from state - Normally in the lifecycle of the app, the User Settings should be loaded on startup
    const storedSetting = this.getStoredSetting();
    if (storedSetting != null) {
      return this.parse(storedSetting.value);
    }

    // Get value from local storage
    const localValue = LocalCache.get(this.key);
    if (localValue != null) {
      if (MIGRATE_LOCAL_CACHE_TO_USER_SETTINGS_API) {
        this.cleanupLocalStorageOnSet();
      }

      return localValue;
    }

    return null;
  }

  public async set(value: T) {
    // If value is null, remove the setting because there is no point in saving a null entry
    if (value == null) {
      await this.delete();
      return;
    }

    // Compare saved value with new value to determine if an update is needed
    const serializedValue = this.stringify(value);
    const storedSetting = this.getStoredSetting();
    const previousSerializedValue = storedSetting?.value;
    const needUpdate = serializedValue !== previousSerializedValue;
    if (!needUpdate) {
      return;
    }
    await this.createUserSetting({
      instances: new UserSetting(this.key, serializedValue),
      showLoader: false,
    });

    if (MIGRATE_LOCAL_CACHE_TO_USER_SETTINGS_API) {
      LocalCache.remove(this.key);
    }
  }

  public async delete() {
    if (this.deleteLocked) {
      // Prevent multiple delete at the same time to avoid errors
      return;
    }
    const storedSetting = this.getStoredSetting();
    if (storedSetting == null) {
      // If there is nothing to delete, don't delete anything
      return;
    }

    try {
      this.deleteLocked = true;
      await this.deleteUserSetting({
        instance: { id: this.key },
        showLoader: false,
        shouldWarnUserOnError: false,
      });

      if (MIGRATE_LOCAL_CACHE_TO_USER_SETTINGS_API) {
        LocalCache.remove(this.key);
      }
    } catch (error) {
      console.error('Failed to delete user setting', error);
    } finally {
      this.deleteLocked = false;
    }
  }
}

export default SettingWrapper;
